import { roundTo2, roundTo2Str } from "./numberUtils";

export const OFFICE_EXPENSE_OPTIONS = [
  { key: "deskFee", label: "Desk fee" },
  { key: "courierFee", label: "Courier fee" },
  { key: "bankCharge", label: "Bank charge", taxExempt: true },
  { key: "otherFee", label: "Other fee" },
  { key: "commissionDeduction", label: "Commission deduction" },
  { key: "garnishment", label: "Garnishment", taxExempt: true },
];

export function emptyOfficeExpenses() {
  return {
    deskFee: { selected: false, amount: "" },
    courierFee: { selected: false, amount: "" },
    bankCharge: { selected: false, amount: "" },
    otherFee: { selected: false, amount: "" },
    commissionDeduction: { selected: false, amount: "" },
    garnishment: { selected: false, amount: "" },
  };
}

/** Normalize stored officeExpenses from API (may be partial). */
export function normalizeOfficeExpenses(raw) {
  const base = emptyOfficeExpenses();
  if (!raw || typeof raw !== "object") return base;
  for (const opt of OFFICE_EXPENSE_OPTIONS) {
    const row = raw[opt.key];
    if (row && typeof row === "object") {
      base[opt.key] = {
        selected: !!(row.selected === true || row.selected === "true"),
        amount: row.amount != null ? String(row.amount) : "",
      };
    }
  }
  return base;
}

/** Decimal rate (e.g. 0.13). Empty agent rate uses defaultTaxRate. */
export function resolveOfficeExpensesTaxRate(agentObj, defaultTaxRate = 0.13) {
  const raw = agentObj?.officeExpensesTaxRate;
  if (raw != null && String(raw).trim() !== "") {
    const n = parseFloat(String(raw).replace(/,/g, ""));
    if (!Number.isNaN(n)) {
      if (n > 1 && n <= 100) return n / 100;
      return n;
    }
  }
  return defaultTaxRate;
}

/** Tax rate for UI display as whole percent (e.g. 13). */
export function officeExpensesTaxRatePercent(agentObj, defaultTaxRate = 0.13) {
  const raw = agentObj?.officeExpensesTaxRate;
  if (raw != null && String(raw).trim() !== "") {
    const n = parseFloat(String(raw).replace(/,/g, ""));
    if (!Number.isNaN(n)) {
      if (n > 1 && n <= 100) return n;
      return Math.round(n * 10000) / 100;
    }
  }
  return Math.round(resolveOfficeExpensesTaxRate(agentObj, defaultTaxRate) * 10000) / 100;
}

/**
 * Sum selected office expense lines (amount + tax per line).
 * @returns {{ totalBase: number, totalTax: number, totalWithTax: number, items: Array, taxRate: number }}
 */
export function computeOfficeExpenses(agentObj, defaultTaxRate = 0.13) {
  const enabled = !!(
    agentObj?.officeExpensesEnabled === true ||
    agentObj?.officeExpensesEnabled === "true"
  );
  if (!enabled) {
    return {
      totalBase: 0,
      totalTax: 0,
      totalWithTax: 0,
      items: [],
      taxRate: defaultTaxRate,
    };
  }

  const rate = resolveOfficeExpensesTaxRate(agentObj, defaultTaxRate);
  const expenses = normalizeOfficeExpenses(agentObj?.officeExpenses);
  const items = [];
  let totalBase = 0;
  let totalTax = 0;

  for (const opt of OFFICE_EXPENSE_OPTIONS) {
    const row = expenses[opt.key];
    const amount = roundTo2(
      parseFloat(String(row.amount ?? "").replace(/,/g, "")) || 0,
    );
    // Include when checked, or when an amount was saved without the line checkbox
    if (!row?.selected && amount <= 0) continue;
    if (amount <= 0) continue;
    const tax = opt.taxExempt ? 0 : roundTo2(amount * rate);
    const total = roundTo2(amount + tax);
    totalBase += amount;
    totalTax += tax;
    items.push({
      key: opt.key,
      label: opt.label,
      amount,
      tax,
      total,
      taxExempt: !!opt.taxExempt,
    });
  }

  return {
    totalBase: roundTo2(totalBase),
    totalTax: roundTo2(totalTax),
    totalWithTax: roundTo2(totalBase + totalTax),
    items,
    taxRate: rate,
  };
}

export function formatOfficeExpensesSummary(agentObj, defaultTaxRate = 0.13) {
  const { items, totalWithTax } = computeOfficeExpenses(
    agentObj,
    defaultTaxRate,
  );
  if (!items.length) return "";
  const parts = items.map(
    (i) => `${i.label} $${roundTo2Str(i.total)}`,
  );
  return `${parts.join("; ")} (total $${roundTo2Str(totalWithTax)})`;
}

/** Preferred stored total, else recompute. */
export function officeExpensesTotalForAgent(agentObj, defaultTaxRate = 0.13) {
  const stored = parseFloat(agentObj?.officeExpensesTotal);
  if (!Number.isNaN(stored) && stored > 0) return roundTo2(stored);
  return computeOfficeExpenses(agentObj, defaultTaxRate).totalWithTax;
}

/**
 * Split so EFT / payment summary can show Commission deduction on its own line.
 * @returns {{ officeExpensesAmount: number, commissionDeductionAmount: number, totalWithTax: number }}
 */
export function officeExpensesSplitForAgent(agentObj, defaultTaxRate = 0.13) {
  const computed = computeOfficeExpenses(agentObj, defaultTaxRate);
  if (computed.items.length > 0) {
    let commissionDeductionAmount = 0;
    let officeExpensesAmount = 0;
    for (const item of computed.items) {
      if (item.key === "commissionDeduction") {
        commissionDeductionAmount = roundTo2(
          commissionDeductionAmount + item.total,
        );
      } else {
        officeExpensesAmount = roundTo2(officeExpensesAmount + item.total);
      }
    }
    return {
      officeExpensesAmount,
      commissionDeductionAmount,
      totalWithTax: computed.totalWithTax,
    };
  }
  const total = officeExpensesTotalForAgent(agentObj, defaultTaxRate);
  return {
    officeExpensesAmount: total,
    commissionDeductionAmount: 0,
    totalWithTax: total,
  };
}

/** Base office expenses (ex tax) for Financial / Net to Office sub-total. */
export function officeExpensesBaseForAgent(agentObj, defaultTaxRate = 0.13) {
  return computeOfficeExpenses(agentObj, defaultTaxRate).totalBase;
}

/** Tax on office expenses for Financial / Net to Office HST column. */
export function officeExpensesTaxForAgent(agentObj, defaultTaxRate = 0.13) {
  return computeOfficeExpenses(agentObj, defaultTaxRate).totalTax;
}

function officeExpenseColumnSide(tradeClassification, agent) {
  const classification = String(tradeClassification || "").toUpperCase();
  if (classification === "CO-OPERATING SIDE") return "selling";
  if (classification === "AGENT DOUBLE ENDER") {
    const tx = String(agent?.transactionType || "").toUpperCase();
    return tx === "SELLING" ? "selling" : "listing";
  }
  return "listing";
}

/** One Financial-sheet row per selected office expense line. */
export function buildOfficeExpenseFinancialRows(
  agent,
  tradeClassification,
  defaultTaxRate = 0.13,
) {
  const { items } = computeOfficeExpenses(agent, defaultTaxRate);
  return items.map((item) => {
    const side = officeExpenseColumnSide(tradeClassification, agent);
    return {
      label: item.label,
      listing: side === "listing" ? item.amount : 0,
      selling: side === "selling" ? item.amount : 0,
      subTotal: item.amount,
      hst: item.tax,
      total: item.total,
    };
  });
}

export function sumOfficeExpensesByColumn(
  agents,
  tradeClassification,
  defaultTaxRate = 0.13,
) {
  let listing = 0;
  let selling = 0;
  let tax = 0;
  for (const agent of agents || []) {
    for (const row of buildOfficeExpenseFinancialRows(
      agent,
      tradeClassification,
      defaultTaxRate,
    )) {
      listing += row.listing;
      selling += row.selling;
      tax += row.hst;
    }
  }
  return {
    listing: roundTo2(listing),
    selling: roundTo2(selling),
    tax: roundTo2(tax),
  };
}

/** Add office expense amounts to Net to Office (when not already included via plan-fee path). */
export function addOfficeExpensesToNetToOfficeValues(
  values,
  agents,
  tradeClassification,
  defaultTaxRate = 0.13,
) {
  const { listing, selling, tax } = sumOfficeExpensesByColumn(
    agents,
    tradeClassification,
    defaultTaxRate,
  );
  if (listing === 0 && selling === 0 && tax === 0) return values;

  const listingN = roundTo2(parseFloat(values.netToOfficeListing || 0) + listing);
  const sellingN = roundTo2(parseFloat(values.netToOfficeSelling || 0) + selling);
  const subN = roundTo2(parseFloat(values.netToOfficeSubTotal || 0) + listing + selling);
  const hstN = roundTo2(parseFloat(values.netToOfficeHST || 0) + tax);
  const totalN = roundTo2(subN + hstN);

  return {
    ...values,
    netToOfficeListing: roundTo2Str(listingN),
    netToOfficeSelling: roundTo2Str(sellingN),
    netToOfficeSubTotal: roundTo2Str(subN),
    netToOfficeHST: roundTo2Str(hstN),
    netToOfficeTotal: roundTo2Str(totalN),
  };
}

/** Agent Financial row totals before office expenses are deducted. */
export function getAgentFinancialTotalsBeforeOfficeExp(
  agent,
  agentSubTotalNum,
  defaultTaxRate = 0.13,
  buyerRebateAmount = 0,
) {
  const amount = parseFloat(agent.amount || 0) || 0;
  const tax = parseFloat(agent.tax || 0) || 0;
  const fees = parseFloat(agent.feesDeducted || 0) || 0;
  const taxOnFees = roundTo2(fees * defaultTaxRate);
  const planFeesWithTax = roundTo2(fees + taxOnFees);
  const gross = roundTo2(amount + tax);
  const agentTotal = roundTo2(gross - planFeesWithTax - buyerRebateAmount);
  const agentHST = roundTo2(agentTotal - agentSubTotalNum);
  return { agentTotal, agentHST };
}
